import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import client from '../lib/feathers';
import HoursControl from './HoursControl';

function formatHolidayDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export default function ListingHolidayHoursEditor({ listingId, servicePath, holidayServicePath, holidays, onHolidaysChanged }) {
  const [holidayPreferences, setHolidayPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', preference: 'closed', reason: '' });
  const [addingHoliday, setAddingHoliday] = useState(false);

  useEffect(() => {
    fetchExistingPreferences();
  }, [listingId]);

  const fetchExistingPreferences = async () => {
    try {
      setLoading(true);
      const service = client.service(servicePath);
      const result = await service.find({
        query: {
          listingId,
          $limit: 100,
        },
      });

      const prefs = {};
      result.data.forEach((record) => {
        prefs[record.holidayId] = {
          id: record.id,
          preference: record.preference,
          reason: record.reason || '',
          hours: record.hours || [],
          holiday: record.Holiday,
        };
      });
      setHolidayPreferences(prefs);
    } catch (e) {
      console.error('Error fetching holiday preferences:', e);
      toast.error('Failed to load holiday preferences');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (holidayId, preference) => {
    try {
      const service = client.service(servicePath);
      const existing = holidayPreferences[holidayId];

      if (preference === 'not_set') {
        if (existing && existing.id) {
          await service.remove(existing.id);
          const newPrefs = { ...holidayPreferences };
          delete newPrefs[holidayId];
          setHolidayPreferences(newPrefs);
          toast.success('Preference removed');
        }
        return;
      }

      const data = {
        listingId,
        holidayId,
        preference,
        reason: existing?.reason || '',
        hours: preference === 'special_hours' ? (existing?.hours || []) : [],
      };

      if (existing && existing.id) {
        const result = await service.patch(existing.id, data);
        setHolidayPreferences({
          ...holidayPreferences,
          [holidayId]: {
            ...existing,
            preference: result.preference,
            reason: result.reason,
            hours: result.hours,
          },
        });
      } else {
        const result = await service.create(data);
        const holiday = holidays.find(h => h.id === holidayId);
        setHolidayPreferences({
          ...holidayPreferences,
          [holidayId]: {
            id: result.id,
            preference: result.preference,
            reason: result.reason,
            hours: result.hours,
            holiday,
          },
        });
      }
      toast.success('Preference updated');
    } catch (e) {
      console.error('Error updating preference:', e);
      toast.error('Failed to update preference');
    }
  };

  const handleReasonChange = async (holidayId, reason) => {
    try {
      const service = client.service(servicePath);
      const existing = holidayPreferences[holidayId];

      if (!existing || !existing.id) return;

      const result = await service.patch(existing.id, { reason });
      setHolidayPreferences({
        ...holidayPreferences,
        [holidayId]: {
          ...existing,
          reason: result.reason,
        },
      });
    } catch (e) {
      console.error('Error updating reason:', e);
      toast.error('Failed to update reason');
    }
  };

  const handleHoursChange = async (holidayId, hourIndex, newHours) => {
    try {
      const service = client.service(servicePath);
      const existing = holidayPreferences[holidayId];

      if (!existing || !existing.id) return;

      const updatedHours = [...(existing.hours || [])];
      updatedHours[hourIndex] = newHours;

      const result = await service.patch(existing.id, { hours: updatedHours });
      setHolidayPreferences({
        ...holidayPreferences,
        [holidayId]: {
          ...existing,
          hours: result.hours,
        },
      });
    } catch (e) {
      console.error('Error updating hours:', e);
      toast.error('Failed to update hours');
    }
  };

  const addHourSegment = async (holidayId) => {
    try {
      const service = client.service(servicePath);
      const existing = holidayPreferences[holidayId];

      if (!existing || !existing.id) return;

      const updatedHours = [...(existing.hours || []), { opens: '09:00', closes: '17:00' }];

      const result = await service.patch(existing.id, { hours: updatedHours });
      setHolidayPreferences({
        ...holidayPreferences,
        [holidayId]: {
          ...existing,
          hours: result.hours,
        },
      });
    } catch (e) {
      console.error('Error adding hour segment:', e);
      toast.error('Failed to add hour segment');
    }
  };

  const removeHourSegment = async (holidayId, hourIndex) => {
    try {
      const service = client.service(servicePath);
      const existing = holidayPreferences[holidayId];

      if (!existing || !existing.id) return;

      const updatedHours = (existing.hours || []).filter((_, idx) => idx !== hourIndex);

      const result = await service.patch(existing.id, { hours: updatedHours });
      setHolidayPreferences({
        ...holidayPreferences,
        [holidayId]: {
          ...existing,
          hours: result.hours,
        },
      });
    } catch (e) {
      console.error('Error removing hour segment:', e);
      toast.error('Failed to remove hour segment');
    }
  };

  const handleAddCustomHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Name and date are required');
      return;
    }

    try {
      setAddingHoliday(true);
      const holidayService = client.service(holidayServicePath);

      // Create the custom holiday
      const holiday = await holidayService.create({
        name: newHoliday.name,
        date: newHoliday.date,
        listingId,
        active: true,
      });

      // Create the listing holiday hours preference
      const lhhService = client.service(servicePath);
      const lhh = await lhhService.create({
        listingId,
        holidayId: holiday.id,
        preference: newHoliday.preference,
        reason: newHoliday.reason || '',
        hours: [],
      });

      setHolidayPreferences({
        ...holidayPreferences,
        [holiday.id]: {
          id: lhh.id,
          preference: lhh.preference,
          reason: lhh.reason,
          hours: lhh.hours || [],
          holiday,
        },
      });

      // Notify parent to refresh holidays list
      if (onHolidaysChanged) {
        onHolidaysChanged();
      }

      setNewHoliday({ name: '', date: '', preference: 'closed', reason: '' });
      setShowAddForm(false);
      toast.success('Custom holiday added');
    } catch (e) {
      console.error('Error adding custom holiday:', e);
      toast.error('Failed to add custom holiday');
    } finally {
      setAddingHoliday(false);
    }
  };

  const handleDeleteCustomHoliday = async (holiday) => {
    if (!window.confirm(`Delete "${holiday.name}"? This will also remove its preference settings.`)) {
      return;
    }

    try {
      const holidayService = client.service(holidayServicePath);
      await holidayService.remove(holiday.id);

      const newPrefs = { ...holidayPreferences };
      delete newPrefs[holiday.id];
      setHolidayPreferences(newPrefs);

      if (onHolidaysChanged) {
        onHolidaysChanged();
      }

      toast.success('Custom holiday deleted');
    } catch (e) {
      console.error('Error deleting custom holiday:', e);
      toast.error('Failed to delete custom holiday');
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading holiday preferences...</div>;
  }

  // Merge holidays with existing preferences (for those not in the holidays prop)
  const allHolidays = [...holidays];
  Object.values(holidayPreferences).forEach((pref) => {
    if (pref.holiday && !allHolidays.find(h => h.id === pref.holiday.id)) {
      allHolidays.push(pref.holiday);
    }
  });

  // Sort by date ascending (upcoming first)
  allHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="space-y-6">
      {/* Add Custom Holiday Button/Form */}
      {!showAddForm ? (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          + Add Custom Holiday
        </button>
      ) : (
        <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Add Custom Holiday</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={newHoliday.name}
                onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                placeholder="e.g., Club Event, Private Wedding"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preference</label>
              <select
                value={newHoliday.preference}
                onChange={(e) => setNewHoliday({ ...newHoliday, preference: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              >
                <option value="closed">Closed</option>
                <option value="special_hours">Special Hours</option>
                <option value="normal_hours">Normal Hours</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={newHoliday.reason}
                onChange={(e) => setNewHoliday({ ...newHoliday, reason: e.target.value })}
                placeholder="e.g., Private event, Staff party"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddCustomHoliday}
                disabled={addingHoliday}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-900 disabled:opacity-50"
              >
                {addingHoliday ? 'Adding...' : 'Add Holiday'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewHoliday({ name: '', date: '', preference: 'closed', reason: '' });
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {allHolidays.length === 0 && !showAddForm && (
        <div className="text-sm text-gray-500">
          No upcoming holidays found. Add a custom holiday or generate system holidays in the Data Management section.
        </div>
      )}

      {allHolidays.map((holiday) => {
        const pref = holidayPreferences[holiday.id];
        const preference = pref?.preference || 'not_set';
        const isCustom = !!holiday.listingId;

        return (
          <div key={holiday.id} className="border-b border-gray-200 pb-6 last:border-b-0">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-gray-900">{holiday.name}</h4>
                  {isCustom && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Custom
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{formatHolidayDate(holiday.date)}</p>
              </div>
              {isCustom && (
                <button
                  type="button"
                  onClick={() => handleDeleteCustomHoliday(holiday)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preference
              </label>
              <select
                value={preference}
                onChange={(e) => handlePreferenceChange(holiday.id, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
              >
                <option value="not_set">Not Set</option>
                <option value="closed">Closed</option>
                <option value="special_hours">Special Hours</option>
                <option value="normal_hours">Normal Hours</option>
              </select>
            </div>

            {(preference === 'closed' || preference === 'special_hours') && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  value={pref?.reason || ''}
                  onChange={(e) => handleReasonChange(holiday.id, e.target.value)}
                  onBlur={(e) => handleReasonChange(holiday.id, e.target.value)}
                  placeholder="e.g., Closed for holiday, Limited staff"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                />
              </div>
            )}

            {preference === 'special_hours' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Hours
                  </label>
                  <button
                    type="button"
                    onClick={() => addHourSegment(holiday.id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Hours
                  </button>
                </div>
                {(pref?.hours || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No hours set. Click "+ Add Hours" to add.</p>
                ) : (
                  <div className="space-y-2">
                    {(pref?.hours || []).map((hour, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <HoursControl
                          value={hour}
                          onChange={(newHours) => handleHoursChange(holiday.id, idx, newHours)}
                        />
                        <button
                          type="button"
                          onClick={() => removeHourSegment(holiday.id, idx)}
                          className="bg-white px-3 pb-1 border border-gray-300 rounded-md shadow-sm text-lg font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          -
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
